import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VerifiedIcon from '@mui/icons-material/Verified';
import useAuth from '../hooks/useAuth';
import {
  getInstitution,
  updateInstitutionSettings,
  listCourseCodes,
  setCourseCode,
  deleteCourseCode,
  listObservers,
  inviteObserver,
  revokeObserver,
  uploadInsuranceCertificate,
  setInsuranceCertificateUrl,
  getSettingsChangelog,
  previewSettingsImpact,
} from '../services/collegeApi';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { ROLES } from '../utils/constants';
import { formatDate, formatDateTime } from '../utils/formatters';

const ROLE_OPTIONS = [
  { value: ROLES.FACULTY, label: 'Faculty Advisor (read-only on students)' },
  { value: ROLES.OBSERVER, label: 'Observer (dashboard + reports only)' },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const [institution, setInstitution] = useState(null);
  const [courseCodes, setCourseCodes] = useState([]);
  const [observers, setObservers] = useState([]);
  const [changelog, setChangelog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState('');

  const refresh = () => {
    if (!user?.college_id) return;
    setLoading(true);
    Promise.all([
      getInstitution(user.college_id).catch(() => null),
      listCourseCodes(user.college_id).catch(() => ({ course_codes: [] })),
      listObservers(user.college_id).catch(() => ({ observers: [] })),
      getSettingsChangelog(user.college_id).catch(() => ({ entries: [] })),
    ])
      .then(([inst, codes, obs, log]) => {
        setInstitution(inst);
        setCourseCodes(codes?.course_codes || []);
        setObservers(obs?.observers || []);
        setChangelog(log?.entries || []);
      })
      .catch((err) => setError(err?.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, [user]);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 0.5 }}>Institution Settings</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Credit defaults, course code mappings, insurance documentation, and team access.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          <CreditDefaultsSection
            institution={institution}
            collegeId={user.college_id}
            onSaved={(msg) => { setSnack(msg); refresh(); }}
            onError={(e) => setError(e?.response?.data?.error || e.message)}
          />
          <CourseCodesSection
            collegeId={user.college_id}
            courseCodes={courseCodes}
            onChange={() => refresh()}
            onSnack={setSnack}
            onError={(e) => setError(e?.response?.data?.error || e.message)}
          />
          <InsuranceSection
            institution={institution}
            collegeId={user.college_id}
            onSaved={(msg) => { setSnack(msg); refresh(); }}
            onError={(e) => setError(e?.response?.data?.error || e.message)}
          />
          <ObserversSection
            collegeId={user.college_id}
            observers={observers}
            onChange={() => refresh()}
            onSnack={setSnack}
            onError={(e) => setError(e?.response?.data?.error || e.message)}
          />
          <ChangelogSection entries={changelog} />
        </Stack>
      )}

      <Snackbar
        open={!!snack}
        autoHideDuration={3000}
        onClose={() => setSnack('')}
        message={snack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

function CreditDefaultsSection({ institution, collegeId, onSaved, onError }) {
  const defaults = institution?.credit_defaults || {};
  const [hours, setHours] = useState(defaults.hours ?? 60);
  const [assessments, setAssessments] = useState(defaults.assessments ?? 6);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const defaultsKey = JSON.stringify(defaults);
  useEffect(() => {
    setHours(defaults.hours ?? 60);
    setAssessments(defaults.assessments ?? 6);
    // defaults is recomputed each render from institution; defaultsKey is its stable signature
    // eslint-disable-next-line
  }, [defaultsKey]);

  const proposed = { hours: Number(hours), assessments: Number(assessments) };
  const hasChanges =
    proposed.hours !== (defaults.hours ?? 60) ||
    proposed.assessments !== (defaults.assessments ?? 6);

  const handleReview = () => setPreviewOpen(true);

  const handleConfirm = async (reason) => {
    setSaving(true);
    try {
      await updateInstitutionSettings(collegeId, {
        credit_defaults: proposed,
        reason,
      });
      onSaved('Credit defaults saved');
      setPreviewOpen(false);
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>Credit defaults</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Default requirements applied to new cohorts. Each cohort can override these.
        Each assessment includes a mandatory mentor review — they're tracked as one count.
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Hours per semester"
            type="number"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            inputProps={{ min: 0 }}
            fullWidth
            helperText="Field hours of supervised assessment work"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Assessments required"
            type="number"
            value={assessments}
            onChange={(e) => setAssessments(e.target.value)}
            inputProps={{ min: 0 }}
            fullWidth
            helperText="Each assessment includes a mandatory mentor review."
          />
        </Grid>
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          variant="contained"
          onClick={handleReview}
          disabled={saving || !hasChanges}
        >
          Review Changes
        </Button>
      </Box>

      <ImpactPreviewDialog
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        onConfirm={handleConfirm}
        collegeId={collegeId}
        currentDefaults={defaults}
        proposed={proposed}
        saving={saving}
      />
    </Paper>
  );
}

function ImpactPreviewDialog({ open, onCancel, onConfirm, collegeId, currentDefaults, proposed, saving }) {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason('');
    setReasonError(false);
    setImpact(null);
    setLoading(true);
    previewSettingsImpact(collegeId, { credit_defaults: proposed })
      .then(setImpact)
      .catch(() => setImpact(null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [open]);

  const handleSubmit = () => {
    if (!reason.trim()) {
      setReasonError(true);
      return;
    }
    onConfirm(reason.trim());
  };

  const fields = [
    { key: 'hours', label: 'Hours per semester' },
    { key: 'assessments', label: 'Assessments required' },
  ].filter((f) => currentDefaults[f.key] !== proposed[f.key]);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Review credit defaults change</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Proposed change
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {fields.map((f) => (
                <Box
                  key={f.key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    backgroundColor: '#f8fafc',
                  }}
                >
                  <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>{f.label}</Typography>
                  <Chip label={currentDefaults[f.key] ?? '—'} size="small" sx={{ backgroundColor: '#e5e7eb' }} />
                  <Typography sx={{ color: 'text.secondary' }}>→</Typography>
                  <Chip label={proposed[f.key]} size="small" color="primary" />
                </Box>
              ))}
            </Stack>
          </Box>

          {loading ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : impact ? (
            <Alert
              severity={impact.may_become_at_risk > 0 ? 'warning' : 'info'}
              sx={{ '& .MuiAlert-message': { width: '100%' } }}
            >
              <Stack spacing={0.5}>
                <Typography variant="body2">
                  This change will affect <strong>{impact.affected_students}</strong> student
                  {impact.affected_students === 1 ? '' : 's'} across {impact.active_cohorts} active cohort
                  {impact.active_cohorts === 1 ? '' : 's'}.
                </Typography>
                {impact.may_become_at_risk > 0 && (
                  <Typography variant="body2">
                    <strong>{impact.may_become_at_risk}</strong> currently on-track student
                    {impact.may_become_at_risk === 1 ? '' : 's'} may move to <strong>At Risk</strong>.
                  </Typography>
                )}
                {impact.now_qualified > 0 && (
                  <Typography variant="body2">
                    <strong>{impact.now_qualified}</strong> previously at-risk student
                    {impact.now_qualified === 1 ? '' : 's'} would now be qualified.
                  </Typography>
                )}
              </Stack>
            </Alert>
          ) : (
            <Alert severity="info">No impact data available.</Alert>
          )}

          <TextField
            label="Reason for change"
            value={reason}
            onChange={(e) => { setReason(e.target.value); setReasonError(false); }}
            multiline
            rows={3}
            fullWidth
            required
            error={reasonError}
            helperText={reasonError
              ? 'A reason is required for the audit log.'
              : 'Recorded in the change history. Visible to coordinators and observers.'}
            placeholder="e.g. Aligning with updated CYB-480 syllabus approved by department chair on 4/8."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving || loading}>
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Confirm Change'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ChangelogSection({ entries }) {
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>Change history</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Audit trail of credit defaults changes. Visible to all team members for accreditation purposes.
      </Typography>
      {entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No changes recorded yet.
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Date</TableCell>
                <TableCell>Field</TableCell>
                <TableCell>Change</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Changed by</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Affected</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((e) => (
                <TableRow key={e.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatDate(e.timestamp)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDateTime(e.timestamp).split(',').slice(1).join(',').trim()}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {e.field_label || e.field}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Chip label={e.old_value} size="small" sx={{ backgroundColor: '#e5e7eb', height: 22 }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>→</Typography>
                      <Chip label={e.new_value} size="small" color="primary" sx={{ height: 22 }} />
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                    <Typography variant="body2">{e.actor_name}</Typography>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top', maxWidth: 320 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      {e.reason}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {e.affected_students} student{e.affected_students === 1 ? '' : 's'}
                    </Typography>
                    {e.became_at_risk > 0 && (
                      <Typography variant="caption" sx={{ color: 'error.main' }}>
                        {e.became_at_risk} → at-risk
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
}

function CourseCodesSection({ collegeId, courseCodes, onChange, onSnack, onError }) {
  const [adding, setAdding] = useState(false);
  const [code, setCode] = useState('');
  const [hours, setHours] = useState(60);
  const [description, setDescription] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const handleAdd = async () => {
    try {
      await setCourseCode(collegeId, {
        course_code: code,
        hours_required: Number(hours),
        description: description || null,
      });
      onSnack('Course code added');
      setAdding(false);
      setCode('');
      setHours(60);
      setDescription('');
      onChange();
    } catch (err) {
      onError(err);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCourseCode(collegeId, confirmDelete);
      onSnack('Course code removed');
      setConfirmDelete(null);
      onChange();
    } catch (err) {
      onError(err);
      setConfirmDelete(null);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Course code mappings</Typography>
          <Typography variant="body2" color="text.secondary">
            Map your registrar's course codes to required hours (e.g. CYB-480 → 60 hours).
          </Typography>
        </Box>
        <Button startIcon={<AddIcon />} onClick={() => setAdding(true)} variant="outlined" size="small">
          Add
        </Button>
      </Box>

      {courseCodes.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No course codes configured.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Course Code</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Hours</TableCell>
              <TableCell width={60}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {courseCodes.map((c) => (
              <TableRow key={c.course_code}>
                <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{c.course_code}</TableCell>
                <TableCell>{c.description || '—'}</TableCell>
                <TableCell align="right">{c.hours_required}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => setConfirmDelete(c.course_code)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={adding} onClose={() => setAdding(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Course Code Mapping</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Course code"
              placeholder="CYB-480"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              fullWidth
              autoFocus
              required
            />
            <TextField
              label="Hours required"
              type="number"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              inputProps={{ min: 0 }}
              fullWidth
              required
            />
            <TextField
              label="Description"
              placeholder="Cybersecurity Practicum I"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdding(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={!code}>Add</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Remove course code?"
        message={`Remove the mapping for ${confirmDelete}? Existing cohorts using this code keep their requirements.`}
        confirmText="Remove"
        confirmColor="error"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </Paper>
  );
}

function InsuranceSection({ institution, collegeId, onSaved, onError }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUploadClick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const presigned = await uploadInsuranceCertificate(collegeId, file.name, file.type);
      await fetch(presigned.upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      await setInsuranceCertificateUrl(collegeId, presigned.public_url);
      onSaved('Insurance certificate uploaded');
    } catch (err) {
      onError(err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const cert = institution?.insurance_certificate || null;

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>Insurance certificate</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload your institution's certificate of insurance. Some clients require this on file before student
        assessors visit.
      </Typography>

      {cert?.url ? (
        <Box sx={{
          p: 2,
          backgroundColor: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
        }}>
          <VerifiedIcon sx={{ color: 'success.main' }} />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>On file</Typography>
            <Typography variant="caption" color="text.secondary">
              Uploaded {formatDate(cert.uploaded_at)}{cert.expires_at ? ` · Expires ${formatDate(cert.expires_at)}` : ''}
            </Typography>
          </Box>
          <Button size="small" startIcon={<OpenInNewIcon />} href={cert.url} target="_blank" rel="noopener">
            View
          </Button>
          <Button size="small" startIcon={<UploadFileIcon />} onClick={handleUploadClick} disabled={uploading} variant="outlined">
            Replace
          </Button>
        </Box>
      ) : (
        <Box>
          <Button
            variant="contained"
            startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
            onClick={handleUploadClick}
            disabled={uploading}
          >
            Upload Certificate
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            PDF or image, max 10 MB
          </Typography>
        </Box>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/*"
        hidden
        onChange={handleFile}
      />
    </Paper>
  );
}

function ObserversSection({ collegeId, observers, onChange, onSnack, onError }) {
  const [inviting, setInviting] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(ROLES.FACULTY);
  const [confirmRevoke, setConfirmRevoke] = useState(null);

  const handleInvite = async () => {
    try {
      await inviteObserver(collegeId, email, role);
      onSnack('Invitation sent');
      setInviting(false);
      setEmail('');
      setRole(ROLES.FACULTY);
      onChange();
    } catch (err) {
      onError(err);
    }
  };

  const handleRevoke = async () => {
    try {
      await revokeObserver(collegeId, confirmRevoke);
      onSnack('Access revoked');
      setConfirmRevoke(null);
      onChange();
    } catch (err) {
      onError(err);
      setConfirmRevoke(null);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Team access</Typography>
          <Typography variant="body2" color="text.secondary">
            Invite faculty advisors and observers (deans, accreditors). Only coordinators can manage settings.
          </Typography>
        </Box>
        <Button startIcon={<AddIcon />} onClick={() => setInviting(true)} variant="outlined" size="small">
          Invite
        </Button>
      </Box>
      <Divider sx={{ my: 1.5 }} />

      {observers.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No team members invited yet. You're the only one with access.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {observers.map((o) => (
            <Box
              key={o.email}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
              }}
            >
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{o.name || o.email}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap>{o.email}</Typography>
              </Box>
              <Chip label={ROLE_OPTIONS.find((r) => r.value === o.role)?.label.split(' (')[0] || o.role} size="small" />
              {o.status && <Chip label={o.status} size="small" variant="outlined" />}
              <IconButton size="small" onClick={() => setConfirmRevoke(o.email)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}

      <Dialog open={inviting} onClose={() => setInviting(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Invite Team Member</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              required
              autoFocus
            />
            <TextField
              select
              label="Role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              fullWidth
            >
              {ROLE_OPTIONS.map((r) => (
                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
              ))}
            </TextField>
            <Alert severity="info">
              An invitation email will be sent. They'll set a password on first sign-in.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviting(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleInvite} disabled={!email}>Send Invite</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!confirmRevoke}
        title="Revoke access?"
        message={`Revoke college portal access for ${confirmRevoke}? Their account stays active but loses portal entry.`}
        confirmText="Revoke"
        confirmColor="error"
        onConfirm={handleRevoke}
        onCancel={() => setConfirmRevoke(null)}
      />
    </Paper>
  );
}
